import React, { useEffect, useState } from "react";
import { TextArea, Button } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import useSyntheticVoice from "../../hooks/useSyntheticVoice";
import useOpenAi from "../../hooks/useOpenAi";
import DemoPlayer from "../SupplierProfile/components/DemoPlayer";

const SupplierSyntheticVoicePage = ({ match }) => {
  const { formatMessage } = useIntl();

  const [syntheticVoiceState, scriptToVoice] = useSyntheticVoice();
  const [openAiState, voiceSuggest, moderateContent, suggestVoiceOverScript] = useOpenAi();

  const [isLoading, setLoading] = useState(false);
  const [syntheticVoice, setSyntheticVoice] = useState(null);
  const [sampleList, setSampleList] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    script: '',
  });

  useEffect(() => {
    loadSampleList();    
    loadSyntheticVoice();
  }, []);

  const handleFileUpload = (event) => {
    setLoading(true);
    const files = event.target.files;
    const data = new FormData();
    for (let i = 0; i < files.length; i++) {
      data.append("files[]", files[i]);
    }

    fetch(`/rest/v2/supplier/${match.params.supplierId}/synthetic-voice`, {
      credentials: 'same-origin',
      method: 'POST',
      body: data,
    })
      .then((response) => response.json())
      .then((syntheticVoiceList) => {
        if (syntheticVoiceList.success === true) {
          setSyntheticVoice(syntheticVoiceList.data[0]);
          loadSampleList();
        } else {
          if (syntheticVoiceList.errors && syntheticVoiceList.errors.length > 0) {
            toastr.error(syntheticVoiceList.errors[0]);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadSampleList = () => {
    fetch(
      `/rest/v2/supplier/${match.params.supplierId}/synthetic-voice-demos`,
      { credentials: "same-origin" }
    )
      .then((response) => response.json())
      .then((syntheticVoiceDemoList) => {
        setSampleList(syntheticVoiceDemoList.data);
      });
  }

  const loadSyntheticVoice = () => {
    fetch(
      `/rest/v2/supplier/${match.params.supplierId}/synthetic-voice`,
      { credentials: "same-origin" }
    )
      .then((response) => response.json())
      .then((syntheticVoice) => {
        if (syntheticVoice.data.length > 0) {
          setSyntheticVoice(syntheticVoice.data[0]);
        }
      });
  };

  const handleSampleUpdate = (event) => {
    setLoading(true);
    const files = event.target.files;
    const data = new FormData();

    for (let i = 0; i < files.length; i++) {
      data.append("files[]", files[i]);
    }

    fetch(`/rest/v2/supplier/${match.params.supplierId}/synthetic-voice-demos`, {
      credentials: "same-origin",
      method: "POST",
      body: data,
    })
      .then((response) => response.json())
      .then(() => {
        loadSampleList();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteSample = (sampleId) => {
    setLoading(true);

    fetch(`/rest/v2/supplier/${match.params.supplierId}/synthetic-voice-demos`, {
      credentials: "same-origin",
      method: "DELETE",
      body: JSON.stringify({
        sample_id: sampleId,
      }),
    }).finally(() => {
      setLoading(false);
      loadSampleList();
    });
  };

  async function generateVoice () {
    const moderation = await moderateContent(form.script);
    if (!moderation.isFlagged && !moderation.containsProfanity && syntheticVoice) {
      const url = await scriptToVoice(syntheticVoice.elevenlabs_voice_id, form.script);
      setHistory((prev) => [{ script: form.script, url }, ...prev]);
      setForm({ ...form, script: '' });

      // To scroll to the bottom of the page
      // and make the new voice visible
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  async function approveVoice() {
    const data = {
      uid: syntheticVoice.uid,
      approved: 1,
    };

    fetch(`/rest/v2/supplier/${match.params.supplierId}/synthetic-voice`, {
      credentials: 'same-origin',
      method: 'PUT',
      body: JSON.stringify(data),
    }).then(() => {
      loadSyntheticVoice();
    });
  }

  return (
    <div className="w-full h-full relative p-8">
      <div className="grid gap-8 grid-cols-2">
        <div>

          {(sampleList && sampleList.length === 0) && (
            <>
              <div className="mb-4 font-bold">
                You have no audio clips
              </div>

              <p>
                Upload clean audio clips to train your AI voice.
              </p>
              <p>
                Once you have uploaded audio clips, you can test your AI voice.
              </p>
            </>
          )}

          {(sampleList && sampleList.length > 0) && (
            <>
              <div className="mb-4 font-bold">
                Test your AI Voice
              </div>

              <div>
                <TextArea
                  className="text-sm"
                  placeholder={formatMessage(messages.scriptPlaceholder)}
                  rows={4}
                  maxLength={200}
                  value={form.script}
                  disabled={syntheticVoiceState.isLoading}
                  onChange={(e) => {
                    setForm({ ...form, script: e.target.value });
                  }}
                />
              </div>


              { !openAiState.isLoading && openAiState.isFlagged ?
                <span className='text-negative hover:text-negative-lighter'><FormattedMessage {...messages.scriptFlagged} /></span>
                : null
              }

              { !openAiState.isLoading && openAiState.containsProfanity ?
                <span className='text-negative hover:text-negative-lighter'><FormattedMessage {...messages.scriptProfanity} /></span>
                : null
              }

              <div className="flex justify-between items-center">
                <p className="w-2/3 text-sm">
                  <FormattedMessage
                    {...messages.legalNotice}
                    values={{ tcs: (chunks) => <a className="text-primary" href="https://www.voicearchive.com/terms-conditions/">{chunks}</a> }}
                  />
                </p>

                <Button
                  className="shrink-0"
                  onClick={() => generateVoice()}
                  disabled={openAiState.isLoading || syntheticVoiceState.isLoading || (!syntheticVoice || syntheticVoice.length === 0)}
                >
                  { openAiState.isLoading || syntheticVoiceState.isLoading ?
                    <span><FormattedMessage {...messages.processing} /></span>
                  :
                    <span><FormattedMessage {...messages.generateVoice} /></span>
                  }
                </Button>
              </div>

              <div>
              { history.map((item, index) => (
                <div className="flex my-4 p-4 border border-primary rounded bg-gray-50" key={index}>
                  <div className="w-1/12">
                    <DemoPlayer src={item.url} />
                  </div>
                  <div className="w-11/12">
                    <p>{item.script}</p>
                  </div>
                </div>

              ))}
              </div>

            </>
          )}
        </div>

        <div>
        {(sampleList && sampleList.length > 0 && syntheticVoice.approved == "0") && (
          <div className="p-4 bg-gray-50 mb-6">
            <div className="mb-2 font-bold">Approve your model</div>
            <p>By approving, you give us permission to display an example of your synthetic voice on our website and clients will be able to choose your synthetic voice for their next project</p>
            <Button
              className="shrink-0 mt-2"
              size="xs"
              onClick={() => approveVoice()}
            >
              <span><FormattedMessage {...messages.approveVoice} /></span> 
            </Button>
          </div>
        )}
          <div className="mb-4">
            <p className="underline">Supported languages:</p>
            <p>English, Japanese, Chinese, German, Hindi, French, Korean, Portuguese, Italian, Spanish, Indonesian, Dutch, Turkish, Filipino, Polish, Swedish, Bulgarian, Romanian, Arabic, Czech, Greek, Finnish, Croatian, Malay, Slovak, Danish, Tamil, Ukrainian, Russian</p>
          </div>

          {(sampleList && sampleList.length > 0) && (
            <div className="mb-4 font-bold">
              Audio clips used for model training
            </div>
          )}

          {!isLoading && sampleList && sampleList.map((sample) => (
            <div>
              <span>{sample.file_name}</span>
              <span
                className="ml-4 cursor-pointer"
                onClick={() => deleteSample(sample.sample_id)}
              >
                Delete
              </span>
            </div>
          ))}

          {isLoading && <div>Loading...</div>}
          {!isLoading && (
            <>
              <div class="mt-4">
                <label class="text-gray-700 font-bold block">Upload clean audio (max 10mb)</label>
                <p class="my-2 text-gray-700 block">Please upload audio that have been recorded in your approved studio. Sample quality is more important than quantity. Noisy samples will give bad results. Providing more than 5 minutes of audio in total brings little improvement.</p>
              </div>
              {!syntheticVoice && (
               <div>
                <p className="mb-4">By uploading your clean audio file, you are opting into our AI program and agreeing to all terms and conditions. Please ensure your file meets our quality standards for the best experience.</p>
                <input type="file" multiple onChange={handleFileUpload} />
               </div>
              )}
              {syntheticVoice && (
               <input type="file" multiple onChange={handleSampleUpdate} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierSyntheticVoicePage;
